import { useEffect, useRef, useState } from "react";
import { ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, assetUrl, formatApiError } from "@/lib/api";

export function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] text-charcoal/60 font-medium">{label}</Label>
      <div className="mt-2">{children}</div>
      {hint && <p className="text-xs text-charcoal/40 mt-1.5">{hint}</p>}
    </div>
  );
}

export function ImageField({ label, value, onChange, testid, hint }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] text-charcoal/60 font-medium">{label}</Label>
      <div className="mt-2 flex gap-4 items-start">
        {value ? (
          <img src={assetUrl(value)} alt="" className="w-32 h-20 object-cover border border-charcoal/10 shrink-0" data-testid={`${testid}-preview`} />
        ) : (
          <div className="w-32 h-20 bg-charcoal/5 border border-charcoal/10 flex items-center justify-center text-charcoal/30 shrink-0">
            <ImageIcon size={18} />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Image URL or upload below"
            className="rounded-none border-charcoal/20 bg-white"
            data-testid={`${testid}-url-input`}
          />
          <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={doUpload} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-none"
            data-testid={`${testid}-upload-button`}
          >
            <Upload size={14} className="mr-2" />
            {uploading ? "Uploading…" : "Upload image"}
          </Button>
          {hint && <p className="text-xs text-charcoal/40">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

export function PhotoSubmissions() {
  const [items, setItems] = useState(null);

  const load = () =>
    api
      .get("/admin/photo-submissions")
      .then((r) => setItems(r.data.submissions))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const remove = (id) => api.delete(`/admin/photo-submissions/${id}`).then(load).catch((e) => toast.error(formatApiError(e)));

  return (
    <div className="bg-white border border-charcoal/10 p-6 md:p-8" data-testid="photos-panel">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif text-xl font-semibold text-charcoal">Photo Submissions</h3>
        <Button variant="outline" size="sm" onClick={load} className="rounded-none" data-testid="photos-refresh-button">
          Refresh
        </Button>
      </div>
      {!items ? (
        <p className="text-sm text-charcoal/50">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-charcoal/50" data-testid="photos-empty">No photo submissions yet. Photos submitted through the announcement popup will appear here.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((s) => (
            <div key={s.id} className="border border-charcoal/10 p-5" data-testid={`photo-submission-${s.id}`}>
              <div className="flex gap-4">
                <a href={assetUrl(s.photo)} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <img src={assetUrl(s.photo)} alt={s.title} className="w-28 h-28 object-cover border border-charcoal/10" />
                </a>
                <div className="min-w-0">
                  <p className="font-medium text-charcoal truncate">{s.title}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-gold font-semibold mt-1">{s.category}</p>
                  <p className="text-sm text-charcoal/70 mt-2">{s.name}</p>
                  <a href={`mailto:${s.email}`} className="text-sm text-vermillion hover:underline">{s.email}</a>
                </div>
              </div>
              <div className="mt-4 space-y-1 text-xs text-charcoal/60">
                {s.instagram && <p>Instagram: {s.instagram}</p>}
                {s.website && <p>Website: <a href={s.website} target="_blank" rel="noopener noreferrer" className="text-vermillion hover:underline break-all">{s.website}</a></p>}
                {s.theyyam && <p>Theyyam: {s.theyyam}</p>}
                {s.temple && <p>Temple: {s.temple}</p>}
                {s.description && <p className="whitespace-pre-wrap">Note: {s.description}</p>}
                <p className="text-charcoal/40">{new Date(s.created_at).toLocaleString()}</p>
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="outline" size="sm" className="rounded-none text-vermillion" onClick={() => remove(s.id)} data-testid={`photo-delete-${s.id}`}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
